import React, { use } from 'react';
import { Card, Row, Col, Container, Button } from 'react-bootstrap';
import style from './Dashboard.module.scss';
import { Tag, Receipt, Send, Download } from 'lucide-react';
import { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement
} from 'chart.js';
import Form from 'react-bootstrap/Form';
import fetchRangeReport from '../../services/report/fetchRangeReport.js';
import getRecentOrders from '../../services/order/getOrder.js';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';

import { Bar, Doughnut } from 'react-chartjs-2';
import fetchDailyReport from '../../services/report/fetchDailyReport';
import fetchMonthlyReport from '../../services/report/fetchMonthlyReport';
import fetchYearlyReport from '../../services/report/fetchYearlyReort.js';
import fetchProducts from '../../services/products/fetchProducts.js';
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement
);

function Dashboard() {
  const [sortParam, setSortParam] = useState('daily');
  const [payloadData, setPayloadData] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [recentOrders, setRecentOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentDays, setPaymentDays] = useState(15);
  const getOrdinal = (n) => {
    if (n > 3 && n < 21) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };

  useEffect(() => {
    const getOrders = async () => {
      try {
        const response = await getRecentOrders();
        const getProducts = await fetchProducts()
        setRecentOrders(response.data);
        setProducts(getProducts.data);
       } catch (error) {
        console.error('Error fetching recent orders:', error);
      } };
    getOrders();
  }, []);

  const cardData = [
    {
      title: 'Total Sales',
      value: payloadData ? `₹${Number(payloadData.totalSales).toFixed(2)}` : '—',
      icon: <Tag size={20} />,
      iconBg: 'purple'
    },
    {
      title: 'Total Orders',
      value: `${payloadData.totalOrders}`,
      icon: <Receipt size={20} />,
      iconBg: 'blue'
    },
    {
      title: 'DineIn Orders',
      orders: `${Number(payloadData?.orderTypeBreakdown?.dineIn?.count)}`,
      total: `₹${Number(payloadData?.orderTypeBreakdown?.dineIn?.total).toFixed(2)}`,
      icon: <Send size={20} />,
      iconBg: 'cyan'
    },
    {
      title: 'Takeaway Orders',
      orders: `${Number(payloadData?.orderTypeBreakdown?.takeAway?.count)}`,
      total: `₹${Number(payloadData?.orderTypeBreakdown?.takeAway?.total).toFixed(2)}`,
      icon: <Download size={20} />,
      iconBg: 'green'
    }
  ];

  const dailyBarData = payloadData?.hourlySales
    ? {
        labels: payloadData.hourlySales.map(h => `${h.hour}:00`),
        datasets: [
          {
            label: 'Sales',
            data: payloadData.hourlySales.map(h =>
              Number(h.sales.toFixed(2))
            ),
            backgroundColor: '#6366f1',
            borderRadius: 6,
            barThickness: 14,
          },
        ],
      }
    : null;

  const monthlyBarData = payloadData?.monthlySales
  ? {
      labels: payloadData.monthlySales.map(d => getOrdinal(d.day)),
      datasets: [
        {
          label: 'Sales',
          data: payloadData.monthlySales.map(d =>
            Number(d.total.toFixed(2))
          ),
          backgroundColor: '#22c55e',
          borderRadius: 6,
          barThickness: 18,
        },
      ],
    }
  : null;

  const yearlyBarData = payloadData?.yearlyMonthlySales
    ? {
        labels: payloadData.yearlyMonthlySales.map(m =>
          new Date(0, m.month - 1).toLocaleString('en', { month: 'short' })
        ),
        datasets: [
          {
            label: 'Sales',
            data: payloadData.yearlyMonthlySales.map(m =>
              Number(m.total.toFixed(2))
            ),
            backgroundColor: '#0ea5e9',
            borderRadius: 6,
            barThickness: 18,
          },
        ],
      }
    : null;

  const rangeBarData =
  payloadData?.dailySales && payloadData?.startDate
    ? (() => {
        const labels = [];
        const data = [];

        const start = new Date(payloadData.startDate);
        const end = new Date(payloadData.endDate);

        Object.entries(payloadData.dailySales).forEach(([day, value]) => {
          const date = new Date(start);
          date.setDate(Number(day));

          labels.push(
            date.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
            })
          );

          data.push(Number(value.total.toFixed(2)));
        });

        return {
          labels,
          datasets: [
            {
              label: 'Sales',
              data,
              backgroundColor: '#f59e0b',
              borderRadius: 6,
              barThickness: 18,
            },
          ],
        };
      })()
    : null;

  useEffect(() => {
    const reports = async () => {
      try {
        if (sortParam === 'monthly') {
          const report = await fetchMonthlyReport();
          setPayloadData(report.data);
          return;
        }

        if (sortParam === 'daily') {
          const report = await fetchDailyReport();
          setPayloadData(report.data);
          return;
        }

        if (sortParam === 'yearly') {
          const report = await fetchYearlyReport();
          setPayloadData(report.data);
          return;
        }
      } catch (error) {
        console.error('Error fetching daily report:', error);
      }};
    reports();
   }, [sortParam]);

    const fetchDataInRange = async() => {
      if(!fromDate || !toDate) {
        toast.warn("Please enter both start and end date");
      }

      try {
        const response = await fetchRangeReport(fromDate, toDate);
        setPayloadData(response.data);
      }
      catch (error) {
        console.error('Error fetching report in range:', error);
      }
    }

    const pieData = payloadData?.itemSales
  ? {
      labels: Object.keys(payloadData.itemSales),
      datasets: [
        {
          label: 'Item Sales',
          data: Object.values(payloadData.itemSales).map(
            item => Number(item.total.toFixed(2))
          ),
          backgroundColor: [
            '#6366f1',
            '#22c55e',
            '#f59e0b',
            '#ef4444',
            '#06b6d4',
          ],
          borderWidth: 0,
        },
      ],
    }
  : null;

  const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-IN');

  const formatAmount = (amount) =>
    `₹${Number(amount).toFixed(2)}`;

  const getStatusClass = (status) => {
    if (status === 'completed') return style.delivered;
    if (status === 'in-progress') return style.progress;
    return style.returned;
  };

  const lineChartData = (() => {
    if (!Array.isArray(recentOrders) || recentOrders.length === 0) return null;

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - paymentDays);

    const dailyTotals = {};

    recentOrders
      .filter(
        o =>
          o.status === 'completed' &&
          new Date(o.createdAt) >= cutoffDate
      )
      .forEach(order => {
        const dateKey = new Date(order.createdAt)
          .toISOString()
          .split('T')[0]; // YYYY-MM-DD

        dailyTotals[dateKey] =
          (dailyTotals[dateKey] || 0) + order.total;
      });

    const sortedDates = Object.keys(dailyTotals).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    return {
      labels: sortedDates.map(d =>
        new Date(d).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
        })
      ),
      datasets: [
        {
          label: 'Payment Received',
          data: sortedDates.map(d =>
            Number(dailyTotals[d].toFixed(2))
          ),
          borderColor: '#6366f1',
          backgroundColor: 'transparent',
          tension: 0.4,
          pointRadius: 3,
        },
      ],
    };
  })();

  return (
    <Container fluid className={style.dashboardContainer}>
    <div className={`${style.selectBox} pb-3`}>
      <Row className="align-items-end g-3 justify-content-end">

        {/* DATE RANGE (ONLY WHEN IN_RANGE) */}
        {sortParam === 'in_range' && (
          <>
            <Col xs={12} sm="auto">
              <Form.Label className="small text-muted">From</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Col>

            <Col xs={12} sm="auto">
              <Form.Label className="small text-muted">To</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Col>

            <Col xs={12} sm="auto">
              <Button
                variant="primary"
                size="sm"
                className="px-4"
                onClick={fetchDataInRange}
                disabled={!fromDate || !toDate}
              >
                Fetch
              </Button>
            </Col>
          </>
        )}

        {/* FILTER DROPDOWN */}
        <Col xs={12} sm="auto">
          <Form.Select
            size="sm"
            value={sortParam}
            onChange={(e) => setSortParam(e.target.value)}
          >
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="in_range">By Date</option>
          </Form.Select>
        </Col>

      </Row>
    </div>
      <Row className="g-2 g-md-4">
        {cardData.map((card, index) => (
          <Col key={index} xs={6} sm={6} lg={3}>
            <Card className={style.cardStyle}>
              <Card.Body>
                {/* ICON */}
                <div className={`${style.iconBox} ${style[card.iconBg]}`}>
                  {card.icon}
                </div>

                {/* TEXT */}
                <p className={style.title}>{card.title}</p>
                {card.value && <h3 className={style.value}>{card.value}</h3>}
                {card.orders && <h5>Orders: {card.orders}</h5>}
                {card.total && <h6>Amount: {card.total}</h6>}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <Row className="g-4 mt-1">
        {/* BAR CHART */}
        <Col lg={8}>
          <Card className={style.chartCard}>
            <div className={style.cardHeader}>
              <h6>Sales & Purchases</h6>
              <span>{sortParam === 'daily' ? 'Today' : sortParam === 'monthly' ? 'Monthly' : 'By Date'}</span>
            </div>
            {sortParam === 'in_range' && rangeBarData && (
              <Bar
                data={rangeBarData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: 'bottom' },
                  },
                  scales: {
                    y: {
                      ticks: {
                        callback: value => `₹${value / 1000}k`,
                      },
                    },
                  },
                }}
              />
            )}

            {sortParam === 'daily' && dailyBarData && (
                <Bar
                  data={dailyBarData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        ticks: {
                          callback: (value) => `₹${value / 1000}k`,
                        },
                      },
                    },
                  }}
                />
              )}

            {sortParam === 'monthly' && monthlyBarData && (
              <Bar
                data={monthlyBarData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: 'bottom' },
                  },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => `₹${value / 1000}k`,
                      },
                    },
                  },
                }}
              />
            )}

            {sortParam === 'yearly' && yearlyBarData && (
              <Bar
                data={yearlyBarData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: 'bottom' },
                  },
                  scales: {
                    y: {
                      ticks: {
                        callback: value => `₹${value / 1000}k`,
                      },
                    },
                  },
                }}
              />
            )}
          </Card>
        </Col>

        {/* PIE CHART */}
      <Col lg={4}>
        <Card className={style.chartCard}>
          <div className={style.cardHeader}>
            <h6>Top Selling Items</h6>
          </div>

          {pieData && (
            <Doughnut
              data={pieData}
              options={{
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      boxWidth: 12,
                      padding: 12,
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const value = ctx.raw;
                        return `₹${value.toLocaleString('en-IN')}`;
                      },
                    },
                  },
                },
                cutout: '65%',
              }}
            />
          )}
        </Card>
      </Col>
      </Row>
      <Row className="g-4 mt-1">
        {/* RECENT INVOICE TABLE */}
        <Col lg={12}>
          <Card className={style.chartCard}>
            <div className={style.cardHeader}>
              <h6>Recent Invoice</h6>
              <span>Sales Invoice</span>
            </div>

            <div className={style.tableWrapper}>
              <table className={style.invoiceTable}>
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Customer</th>
                    <th>Sales Date</th>
                    <th>Paid Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => (
                    <tr key={order._id}>
                      <td>{order.invoiceNumber}</td>
                      <td>
                        {order.mobileNumber || 'Walk-in'}
                      </td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>{formatAmount(order.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>
      <Row className="g-4 mt-1">
  {/* PAYMENTS LINE CHART */}
      <Col lg={8}>
        <Card className={style.chartCard}>
          <div className={style.cardHeader}>
            <h6>Payments</h6>

            <Form.Select
              size="sm"
              value={paymentDays}
              onChange={(e) => setPaymentDays(Number(e.target.value))}
              style={{ width: '110px' }}
            >
              <option value={7}>7 Days</option>
              <option value={15}>15 Days</option>
              <option value={30}>30 Days</option>
              <option value={60}>60 Days</option>
            </Form.Select>
          </div>
          {lineChartData && (
            <Line
              data={lineChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'bottom' },
                },
                scales: {
                  y: {
                    ticks: {
                      callback: value => `₹${value / 1000}k`,
                    },
                  },
                },
              }}
            />
          )}
        </Card>
      </Col>

      {/* STOCK ALERT */}
      <Col lg={4}>
        <Card className={style.chartCard}>
          <div className={style.cardHeader}>
            <h6>Stock Alert</h6>
          </div>
          <div className={style.stockAlert}>
            {[...products]
              .sort(
                (a, b) =>
                  (a.stockQuantity - a.lowStockThreshold) -
                  (b.stockQuantity - b.lowStockThreshold)
              )
              .map(p => (
                <div key={p._id} className={style.stockRow}>
                  <span>• {p.name}</span>
                  <span
                    className={`${style.qty} ${
                      p.stockQuantity < p.lowStockThreshold ? style.low : style.ok
                    }`}
                  >
                    {p.stockQuantity}
                  </span>
                </div>
              ))}
          </div>
        </Card>
      </Col>
    </Row>
    </Container>
  );
}

export default Dashboard;